import React, { useState } from 'react';
import JSZip from 'jszip';
import Parse from '../../parseConfig';
import { createBuiltHomeAcl, getBuiltHomeAccessRoles } from './builtHomeAccess';
import { cleanImportWarnings, parseCoordinates, parseDateInput } from './builtProjectUtils';
import {
  isApprovedSharedAddressMatch,
  isCuratedSiblingRecord,
  prepareBuiltImportEntry,
  validateBuiltImportManifest,
} from './builtImportUtils';
import useBuiltModalEscape from './useBuiltModalEscape';

const BuiltHome = Parse.Object.extend('BuiltHome');

const findByField = async (field, value) => {
  if (!value) return null;
  const query = new Parse.Query(BuiltHome);
  query.equalTo(field, value);
  return query.first();
};

const geocodeAddress = async (address) => {
  if (!address) return null;
  if (!window.google?.maps?.Geocoder) throw new Error('The map is still loading.');
  const response = await new window.google.maps.Geocoder().geocode({ address });
  const location = response.results?.[0]?.geometry?.location;
  if (!location) throw new Error('The address could not be located.');
  return { latitude: location.lat(), longitude: location.lng() };
};

const setOptionalString = (object, field, value) => {
  const trimmed = String(value || '').trim();
  if (trimmed) object.set(field, trimmed);
};

const downloadJson = (value, fileName) => {
  const url = URL.createObjectURL(new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export default function BuiltHomeImportModal({ isOpen, onClose = () => {}, onHomesChanged = async () => {} }) {
  const [zip, setZip] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState({ complete: 0, total: 0 });
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);

  const reset = () => {
    setZip(null);
    setManifest(null);
    setFileName('');
    setProgress({ complete: 0, total: 0 });
    setError('');
    setResults(null);
    setReplaceExisting(false);
  };

  const close = () => {
    if (isImporting) return;
    reset();
    onClose();
  };

  useBuiltModalEscape(isOpen, close);

  if (!isOpen) return null;

  const loadPackage = async (file) => {
    if (!file) return;
    setIsLoading(true);
    setError('');
    setResults(null);
    try {
      const loadedZip = await JSZip.loadAsync(file);
      const manifestFile = loadedZip.file('manifest.json');
      if (!manifestFile) throw new Error('The ZIP does not contain manifest.json.');
      const parsedManifest = validateBuiltImportManifest(JSON.parse(await manifestFile.async('string')));
      setZip(loadedZip);
      setManifest(parsedManifest);
      setFileName(file.name);
      setProgress({ complete: 0, total: parsedManifest.entries.length });
    } catch (loadError) {
      console.error('Failed to load built-home package:', loadError);
      setZip(null);
      setManifest(null);
      setError(loadError?.message || 'The import package could not be read.');
    } finally {
      setIsLoading(false);
    }
  };

  const importPlansPdf = async (entry, homeObject) => {
    let plansPdfImported = false;
    const warnings = [];
    if (entry.plansPdf?.path && !homeObject.get('plansPdf')) {
      const zippedPdf = zip.file(entry.plansPdf.path);
      if (!zippedPdf) {
        warnings.push(`Plans PDF missing from package: ${entry.plansPdf.path}`);
      } else {
        try {
          const blob = await zippedPdf.async('blob');
          const safeName = entry.plansPdf.fileName || entry.plansPdf.path.split('/').pop() || 'consent-plans.pdf';
          const parseFile = new Parse.File(safeName, new File([blob], safeName, { type: 'application/pdf' }));
          await parseFile.save();
          homeObject.set('plansPdf', parseFile);
          await homeObject.save();
          plansPdfImported = true;
        } catch (pdfError) {
          console.error('Failed to import plans PDF:', pdfError);
          warnings.push(`Plans PDF upload failed: ${entry.plansPdf.path}`);
        }
      }
    }

    return { plansPdfImported, warnings };
  };

  const importEntry = async (rawEntry, accessRoles) => {
    const entry = prepareBuiltImportEntry(rawEntry);
    const processedAt = new Date().toISOString();
    const baseResult = {
      sourceFolderName: entry.sourceFolderName,
      sourceImportKey: entry.sourceImportKey,
      clientName: entry.clientName,
      address: entry.address,
      consentedDate: entry.consentedDate || '',
      sourcePlanPath: entry.sourcePlanPath || '',
      processedAt,
    };

    const keyMatch = await findByField('sourceImportKey', entry.sourceImportKey);
    const documentMatch = keyMatch || await findByField('sourceDocumentHash', entry.sourceDocumentHash);
    const hashMatch = !keyMatch && isCuratedSiblingRecord(entry, documentMatch) ? null : documentMatch;
    const replacementTarget = replaceExisting && keyMatch?.get('sourceFolderName') ? keyMatch : null;
    if (hashMatch && !replacementTarget) {
      hashMatch.setACL(createBuiltHomeAcl(accessRoles));
      const pdfResult = await importPlansPdf(entry, hashMatch);
      const existingWarnings = cleanImportWarnings(hashMatch.get('importWarnings'));
      const updatedWarnings = [...new Set([...existingWarnings, ...pdfResult.warnings])];
      if (JSON.stringify(updatedWarnings) !== JSON.stringify(hashMatch.get('importWarnings') || [])) {
        if (updatedWarnings.length) hashMatch.set('importWarnings', updatedWarnings);
        else hashMatch.unset('importWarnings');
      }
      await hashMatch.save();
      return { ...baseResult, status: 'linked', builtHomeObjectId: hashMatch.id, plansPdfImported: pdfResult.plansPdfImported, warnings: pdfResult.warnings };
    }

    const addressMatch = await findByField('addressKey', entry.addressKey);
    const isApprovedSharedAddress = isApprovedSharedAddressMatch(entry, addressMatch);
    if (addressMatch && addressMatch.id !== replacementTarget?.id && !isApprovedSharedAddress) {
      return {
        ...baseResult,
        status: 'duplicate-conflict',
        builtHomeObjectId: addressMatch.id,
        plansPdfImported: false,
        warnings: ['An existing BuiltHome has the same normalized address. Link it manually before importing this folder.'],
      };
    }

    const homeObject = new BuiltHome();
    homeObject.setACL(createBuiltHomeAcl(accessRoles));
    homeObject.set('needsReview', 1);
    setOptionalString(homeObject, 'address', entry.address);
    setOptionalString(homeObject, 'addressKey', entry.addressKey);
    setOptionalString(homeObject, 'clientName', entry.clientName);
    setOptionalString(homeObject, 'buildStyle', entry.buildStyle);
    setOptionalString(homeObject, 'sourceImportKey', entry.sourceImportKey);
    setOptionalString(homeObject, 'sourceFolderName', entry.sourceFolderName);
    setOptionalString(homeObject, 'sourceJobNumber', entry.sourceJobNumber);
    setOptionalString(homeObject, 'sourcePlanPath', entry.sourcePlanPath);
    setOptionalString(homeObject, 'sourceDocumentHash', entry.sourceDocumentHash);

    const warnings = [...entry.importWarnings];
    if (entry.consentedDate) homeObject.set('consentedDate', parseDateInput(entry.consentedDate));

    let manualCoordinates = null;
    try {
      if (typeof entry.coordinates === 'string') manualCoordinates = parseCoordinates(entry.coordinates);
      else if (entry.coordinates?.latitude !== undefined && entry.coordinates?.longitude !== undefined) manualCoordinates = entry.coordinates;
    } catch (coordinateError) {
      warnings.push(coordinateError.message);
    }

    if (manualCoordinates) {
      homeObject.set('geocodedCoordinates', new Parse.GeoPoint(manualCoordinates));
    } else if (entry.address) {
      try {
        homeObject.set('geocodedCoordinates', new Parse.GeoPoint(await geocodeAddress(entry.address)));
      } catch (geocodeError) {
        warnings.push(`Address was not geocoded: ${geocodeError.message}`);
      }
    } else {
      warnings.push('No address or coordinates were extracted; this record will not appear as a map marker.');
    }

    homeObject.set('importWarnings', [...new Set(warnings)]);
    await homeObject.save();

    const pdfResult = await importPlansPdf(entry, homeObject);
    if (pdfResult.warnings.length) {
      warnings.push(...pdfResult.warnings);
      homeObject.set('importWarnings', [...new Set(warnings)]);
      await homeObject.save();
    }

    let replacementBackup = null;
    if (replacementTarget) {
      if (pdfResult.warnings.length) {
        await homeObject.destroy();
        throw new Error('Replacement plans PDF did not import cleanly; the existing BuiltHome was left unchanged.');
      }

      replacementBackup = {
        home: replacementTarget.toJSON(),
      };
      await replacementTarget.destroy();
    }

    return {
      ...baseResult,
      status: replacementTarget ? 'replaced' : (warnings.length ? 'incomplete' : 'imported'),
      builtHomeObjectId: homeObject.id,
      replacedBuiltHomeObjectId: replacementTarget?.id || '',
      replacementBackup,
      plansPdfImported: pdfResult.plansPdfImported,
      warnings: [...new Set(warnings)],
    };
  };

  const runImport = async () => {
    if (!manifest || !zip || isImporting) return;
    setIsImporting(true);
    setError('');
    setResults(null);

    try {
      const accessRoles = await getBuiltHomeAccessRoles();

      const entryResults = [];
      for (let index = 0; index < manifest.entries.length; index += 1) {
        try {
          entryResults.push(await importEntry(manifest.entries[index], accessRoles));
        } catch (entryError) {
          console.error('Built-home import entry failed:', entryError);
          const entry = manifest.entries[index];
          entryResults.push({
            sourceFolderName: entry.sourceFolderName,
            sourceImportKey: entry.sourceImportKey,
            clientName: entry.clientName || entry.sourceFolderName,
            address: entry.address || '',
            consentedDate: entry.consentedDate || '',
            sourcePlanPath: entry.sourcePlanPath || '',
            processedAt: new Date().toISOString(),
            status: 'failed',
            builtHomeObjectId: '',
            plansPdfImported: false,
            warnings: [entryError?.message || 'Import failed.'],
          });
        }
        setProgress({ complete: index + 1, total: manifest.entries.length });
      }

      const resultManifest = {
        version: 1,
        sourcePackage: fileName,
        completedAt: new Date().toISOString(),
        entries: entryResults,
      };
      setResults(resultManifest);
      await onHomesChanged();
      downloadJson(resultManifest, 'built-home-import-results.json');
    } catch (importError) {
      console.error('Built-home import failed:', importError);
      setError(importError?.message || 'The import could not be completed.');
    } finally {
      setIsImporting(false);
    }
  };

  const statusCounts = results?.entries?.reduce((counts, result) => {
    counts[result.status] = (counts[result.status] || 0) + 1;
    return counts;
  }, {}) || {};

  return (
    <div className="stock-admin-overlay built-import-overlay" onClick={close}>
      <div className="built-import-panel" onClick={(event) => event.stopPropagation()}>
        <header className="built-review-header">
          <div>
            <div className="stock-admin-title">Import Built Homes</div>
            <div className="built-review-progress">Upload a package created by the warranty-folder scanner.</div>
          </div>
          <button type="button" className="stock-admin-close-button built-admin-close-button" onClick={close} aria-label="Close import">×</button>
        </header>

        <div className="built-import-content">
          <label className="built-import-dropzone">
            <input type="file" accept=".zip,application/zip" disabled={isImporting || isLoading} onChange={(event) => { loadPackage(event.target.files?.[0]); event.target.value = ''; }} />
            <strong>{isLoading ? 'Reading package…' : fileName || 'Choose import ZIP'}</strong>
            <span>The package is validated before anything is written to Parse.</span>
          </label>

          {manifest && !results && (
            <>
              <div className="built-import-summary">
                <div><strong>{manifest.entries.length}</strong><span>client folders</span></div>
                <div><strong>{manifest.entries.filter((entry) => (entry.plansPdf || entry.evidencePdf)?.path).length}</strong><span>plans PDFs</span></div>
                <div><strong>{manifest.entries.filter((entry) => entry.importWarnings?.length).length}</strong><span>with warnings</span></div>
              </div>
              <label className="built-import-replace-option">
                <input type="checkbox" checked={replaceExisting} onChange={(event) => setReplaceExisting(event.target.checked)} />
                <span><strong>Replace matched imported homes</strong> Create and verify each replacement before deleting its previous BuiltHome. Manual homes and address-only matches are never replaced.</span>
              </label>
            </>
          )}

          {isImporting && <div className="built-import-progress"><div style={{ width: `${progress.total ? (progress.complete / progress.total) * 100 : 0}%` }} /><span>{progress.complete} of {progress.total}</span></div>}

          {results && (
            <div className="built-import-results">
              <strong>Import complete</strong>
              <div>{Object.entries(statusCounts).map(([status, count]) => <span key={status}>{count} {status}</span>)}</div>
              <button type="button" className="stock-admin-secondary-button" onClick={() => downloadJson(results, 'built-home-import-results.json')}>Download Results Again</button>
            </div>
          )}

          {error && <div className="stock-admin-error">{error}</div>}
        </div>

        <footer className="stock-admin-footer">
          <div className="stock-admin-footer-status">{replaceExisting ? 'Exact source-key matches are safely replaced; manual homes and address-only matches are untouched.' : 'Exact source-key and document-hash matches are linked; address-only matches are not imported.'}</div>
          <div className="stock-admin-footer-actions">
            <button type="button" className="stock-admin-secondary-button" onClick={close} disabled={isImporting}>Close</button>
            <button type="button" className="stock-admin-primary-button" onClick={runImport} disabled={!manifest || isImporting || Boolean(results)}>{isImporting ? 'Importing…' : 'Import Package'}</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
